import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { UIMessage } from 'ai';

type AnyMessagePart = UIMessage['parts'][number];

/**
 * A chat-pane segment. The chat pane only ever shows assistant prose and status notes — all entity and
 * tool data renders in the workspace pane. {@link buildChatSegments} is the single chokepoint that
 * enforces this for every chat surface (media-plan + line-item), so no tool/entity JSON can leak into a
 * chat bubble.
 */
export type ChatSegment = { type: 'text'; texts: string[] } | { type: 'system-note'; text: string };

/** Collapse a message's parts down to consecutive text runs + system notes, dropping everything else. */
export function buildChatSegments(parts: ReadonlyArray<AnyMessagePart>): ChatSegment[] {
  const segments: ChatSegment[] = [];
  let pendingTexts: string[] = [];

  function flushText() {
    if (pendingTexts.length > 0) {
      segments.push({ type: 'text', texts: [...pendingTexts] });
      pendingTexts = [];
    }
  }

  for (const part of parts) {
    if (part.type === 'text') {
      pendingTexts.push(part.text);
    } else if (part.type === 'data-system-note') {
      flushText();
      segments.push({ type: 'system-note', text: (part as { data: { text: string } }).data.text });
    }
  }

  flushText();
  return segments;
}

export function PlainTextParts({ parts }: { parts: AnyMessagePart[] }) {
  return (
    <>
      {parts.map((p, i) =>
        p.type === 'text' ? (
          <div key={i} className="whitespace-pre-wrap">
            {p.text}
          </div>
        ) : null
      )}
    </>
  );
}

export function UserBubble({ parts }: { parts: AnyMessagePart[] }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[70%] space-y-2 rounded-2xl bg-primary px-4 py-2 text-sm text-primary-foreground shadow-sm">
        <PlainTextParts parts={parts} />
      </div>
    </div>
  );
}

export function TextBubble({ texts }: { texts: string[] }) {
  return (
    <div className="flex justify-start">
      <div className="prose prose-sm max-w-none max-w-[70%] space-y-2 rounded-2xl bg-muted px-4 py-2 text-sm text-foreground shadow-sm">
        {texts.map((t, ti) => (
          <ReactMarkdown key={ti} remarkPlugins={[remarkGfm]}>
            {t}
          </ReactMarkdown>
        ))}
      </div>
    </div>
  );
}

export function SystemNoteLine({ text }: { text: string }) {
  return (
    <div className="flex justify-start">
      <div className="max-w-[80%] text-xs italic text-muted-foreground">{text}</div>
    </div>
  );
}

export function ThinkingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="rounded-2xl bg-muted px-4 py-2 text-sm text-muted-foreground">
        Thinking...
      </div>
    </div>
  );
}

export function WorkspaceThinkingBanner() {
  return (
    <div className="border-b border-primary/20 bg-primary/5 px-6 py-2">
      <div className="flex items-center gap-2 text-xs font-medium text-primary">
        <span className="flex gap-1" aria-hidden>
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary [animation-delay:-0.3s]" />
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary [animation-delay:-0.15s]" />
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
        </span>
        Updating workspace...
      </div>
    </div>
  );
}

export function SuggestionButtons({
  id,
  options,
  onSelect,
  disabled,
  selectedValue
}: {
  id: string;
  options: string[];
  onSelect: (option: string, id: string) => void;
  disabled: boolean;
  selectedValue?: string;
}) {
  const isResolved = selectedValue !== undefined;

  // Quiet pill chips: conversation shortcuts, deliberately lighter than any commit-card button —
  // only commit cards carry a solid button (see the commit-card taxonomy plan).
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option, i) => {
        const isSelected = option === selectedValue;
        const isDisabled = disabled || isResolved;

        return (
          <button
            key={i}
            onClick={() => onSelect(option, id)}
            disabled={isDisabled}
            className={
              isSelected
                ? 'rounded-full border border-primary bg-primary/10 px-3 py-1 text-xs font-medium text-primary'
                : isResolved
                  ? 'rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-400'
                  : 'rounded-full border border-gray-300 bg-transparent px-3 py-1 text-xs font-medium text-gray-600 transition-colors hover:border-primary/50 hover:text-primary disabled:cursor-not-allowed disabled:opacity-50'
            }>
            {option}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The workspace's answer surface for a pending presentOptions question: the agent's question, its
 * option chips (when it offered any), and a free-text answer box — so every answer the agent needs
 * is given HERE, keeping the chat input for the user's own ad-hoc messages. Shows only the latest
 * unanswered prompt.
 */
export function WorkspaceSuggestions({
  id,
  question,
  options,
  multiSelect,
  onSelect,
  disabled
}: {
  id: string;
  question?: string;
  options: string[];
  /** The user may pick several options: chips toggle, and one submit sends the whole set. */
  multiSelect?: boolean;
  onSelect: (option: string, id: string) => void;
  disabled: boolean;
}) {
  const [answer, setAnswer] = React.useState('');
  const [picked, setPicked] = React.useState<ReadonlySet<string>>(new Set());
  const isMulti = Boolean(multiSelect) && options.length > 0;

  function togglePick(option: string) {
    setPicked(prev => {
      const next = new Set(prev);
      if (!next.delete(option)) next.add(option);
      return next;
    });
  }

  function handleAnswerSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (disabled) return;
    const text = answer.trim();
    // Multi: one combined answer — the picked options (display order) plus any free text. '; '
    // separates picks because option labels themselves can contain commas (e.g. "$7,145").
    const chosen = isMulti ? options.filter(o => picked.has(o)) : [];
    const combined = chosen.length
      ? text
        ? `${chosen.join('; ')} — ${text}`
        : chosen.join('; ')
      : text;
    if (!combined) return;
    onSelect(combined, id);
    setAnswer('');
    setPicked(new Set());
  }

  return (
    <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
      <div className="mb-2 text-xs font-medium text-gray-600">The assistant needs your input</div>
      {question ? <div className="mb-2 text-sm font-medium text-gray-800">{question}</div> : null}
      {isMulti ? (
        <>
          <div className="mb-2 text-xs text-gray-500">Pick one or more, then send your answer.</div>
          <div className="flex flex-wrap gap-2">
            {options.map(option => {
              const isPicked = picked.has(option);
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => togglePick(option)}
                  disabled={disabled}
                  aria-pressed={isPicked}
                  className={
                    isPicked
                      ? 'rounded-full border border-primary bg-primary/10 px-3 py-1 text-xs font-medium text-primary'
                      : 'rounded-full border border-gray-300 bg-transparent px-3 py-1 text-xs font-medium text-gray-600 transition-colors hover:border-primary/50 hover:text-primary disabled:cursor-not-allowed disabled:opacity-50'
                  }>
                  {option}
                </button>
              );
            })}
          </div>
        </>
      ) : options.length > 0 ? (
        <SuggestionButtons id={id} options={options} onSelect={onSelect} disabled={disabled} />
      ) : null}
      <form onSubmit={handleAnswerSubmit} className="mt-2 flex items-center gap-2">
        <input
          value={answer}
          onChange={e => setAnswer(e.target.value)}
          placeholder={
            isMulti
              ? 'Add details (optional)...'
              : options.length > 0
                ? 'Or type your own answer...'
                : 'Type your answer...'
          }
          disabled={disabled}
          className="h-8 flex-1 rounded-md border border-gray-300 bg-white px-2 text-sm placeholder:text-gray-400 disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={disabled || (isMulti ? picked.size === 0 && !answer.trim() : !answer.trim())}
          className="h-8 rounded-md border border-gray-300 bg-white px-3 text-xs font-medium text-gray-700 transition-colors hover:border-primary/50 hover:text-primary disabled:cursor-not-allowed disabled:opacity-50">
          Answer
        </button>
      </form>
    </div>
  );
}

export type WorkspacePendingQuestion = {
  id: string;
  question?: string;
  options: string[];
  multiSelect?: boolean;
};

/**
 * The workspace's pending question for the LAST assistant message: the latest unanswered
 * presentOptions call — or, when the model asked in chat prose instead (the prompts forbid it,
 * but models drift), a synthesized open question so the workspace ALWAYS offers a way to answer.
 * The fallback waits for the turn to finish (`isBusy` — streaming text isn't final) and carries
 * no options, so WorkspaceSuggestions renders the question with just the free-text answer box.
 */
export function pendingWorkspaceQuestion(
  messages: ReadonlyArray<{ id: string; role: string; parts: ReadonlyArray<unknown> }>,
  isBusy: boolean
): WorkspacePendingQuestion | null {
  const last = messages[messages.length - 1];
  if (!last || last.role !== 'assistant') return null;
  let result: WorkspacePendingQuestion | null = null;
  let lastText = '';
  for (const raw of last.parts) {
    const part = raw as {
      type?: string;
      text?: string;
      id?: unknown;
      data?: { question?: string; options?: string[]; multi_select?: boolean };
    };
    if (part.type === 'data-suggestions' && part.data) {
      result = {
        id: part.id !== undefined ? String(part.id) : 'suggestions',
        question: part.data.question,
        options: part.data.options ?? [],
        multiSelect: part.data.multi_select
      };
    } else if (part.type === 'text' && part.text) {
      lastText = part.text;
    }
  }
  if (result) return result;
  if (isBusy) return null;
  const question = trailingQuestion(lastText);
  return question ? { id: `${last.id}:chat-question`, question, options: [] } : null;
}

/**
 * The final question sentence of a chat message, when the message effectively ends on one. A '?'
 * buried far before the end (> 200 chars of trailing commentary) has usually been superseded by
 * what the agent said after it, so it does not count. Best-effort sentence extraction — the full
 * question is always visible in chat; this only labels the workspace answer box.
 */
export function trailingQuestion(text: string): string | null {
  const qEnd = text.lastIndexOf('?');
  if (qEnd === -1 || text.length - qEnd - 1 > 200) return null;
  const before = text.slice(0, qEnd);
  const start = Math.max(
    before.lastIndexOf('.'),
    before.lastIndexOf('!'),
    before.lastIndexOf('?'),
    before.lastIndexOf('\n')
  );
  const question = text.slice(start + 1, qEnd + 1).trim();
  return question.length > 2 ? question : null;
}

export function WorkspaceEmptyState({ stepLabel }: { stepLabel?: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center text-center">
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
        <svg
          className="h-6 w-6 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
          />
        </svg>
      </div>
      <p className="text-sm font-medium text-gray-500">AI is gathering context...</p>
      {stepLabel ? <p className="mt-1 text-xs text-gray-400">{stepLabel}</p> : null}
    </div>
  );
}

export function WorkspaceFooterWarning() {
  return (
    <div className="border-t bg-amber-50/60 px-4 py-2">
      <div className="flex items-center gap-2 text-xs text-amber-700">
        <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
          />
        </svg>
        Actions in Workspace modify live data
      </div>
    </div>
  );
}

type ToolPartView = {
  name: string;
  toolCallId: string;
  state: string;
  input?: unknown;
  output?: unknown;
  errorText?: string;
};

const MAX_WORKING_JSON_CHARS = 2000;

// Shared harness tools that drive the chat UI itself (stepper, suggestion buttons, links) rather than
// doing agent work. They're noise in the "working" log, so hide them.
const HIDDEN_WORKING_TOOLS = new Set(['updateStep', 'presentOptions', 'showLink']);

function capJson(value: unknown): string {
  let text: string;
  try {
    text = JSON.stringify(value, null, 2);
  } catch {
    text = String(value);
  }
  if (typeof text !== 'string') return '';
  return text.length > MAX_WORKING_JSON_CHARS
    ? `${text.slice(0, MAX_WORKING_JSON_CHARS)}\n… (truncated)`
    : text;
}

function toolStateBadge(state: string): { label: string; className: string } {
  if (state === 'output-error') return { label: 'error', className: 'bg-red-100 text-red-700' };
  if (state === 'output-available')
    return { label: 'done', className: 'bg-green-100 text-green-700' };
  return { label: 'running', className: 'bg-amber-100 text-amber-700' };
}

/**
 * Collapsible "agent working" log for one assistant message: lists the tool calls in the message's
 * parts (name, state, input, output / error). Read-only, metadata styling; returns null when the
 * message has no tool calls. Gated by the modal's "Show working" toggle — purely for transparency /
 * debugging. The tool parts are already in the stream; this just renders what the chat otherwise drops.
 */
export function AgentWorkingDisclosure({ parts }: { parts: AnyMessagePart[] }) {
  const tools: ToolPartView[] = parts
    .filter(p => typeof p.type === 'string' && p.type.startsWith('tool-'))
    .map(p => {
      const tp = p as unknown as {
        type: string;
        toolCallId: string;
        state: string;
        input?: unknown;
        output?: unknown;
        errorText?: string;
      };
      return {
        name: tp.type.slice('tool-'.length),
        toolCallId: tp.toolCallId,
        state: tp.state,
        input: tp.input,
        output: tp.output,
        errorText: tp.errorText
      };
    })
    .filter(t => !HIDDEN_WORKING_TOOLS.has(t.name));

  if (tools.length === 0) return null;

  return (
    <details className="rounded-lg border border-gray-200 bg-gray-50/60 px-3 py-2 text-xs text-gray-600">
      <summary className="cursor-pointer select-none font-medium text-gray-500">
        Agent working · {tools.length} tool call{tools.length === 1 ? '' : 's'}
      </summary>
      <div className="mt-2 space-y-2">
        {tools.map((t, i) => {
          const badge = toolStateBadge(t.state);
          return (
            <div
              key={`${t.toolCallId}-${i}`}
              className="rounded border border-gray-200 bg-white p-2">
              <div className="flex items-center gap-2">
                <span className="font-mono font-medium text-gray-700">{t.name}</span>
                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${badge.className}`}>
                  {badge.label}
                </span>
              </div>
              {t.input !== undefined ? (
                <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-words text-[11px] text-gray-500">
                  {capJson(t.input)}
                </pre>
              ) : null}
              {t.state === 'output-error' ? (
                <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-words text-[11px] text-red-600">
                  {t.errorText ?? 'error'}
                </pre>
              ) : t.output !== undefined ? (
                <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-words text-[11px] text-gray-500">
                  {capJson(t.output)}
                </pre>
              ) : null}
            </div>
          );
        })}
      </div>
    </details>
  );
}

export type TextSegment = { type: 'text'; texts: string[] };

export function flushPendingTexts(segments: { type: string }[], pendingTexts: string[]): string[] {
  if (pendingTexts.length > 0) {
    segments.push({ type: 'text', texts: [...pendingTexts] } as TextSegment);
  }
  return [];
}
